import type { SolarProposalFinancingDto } from "~/api/dto/solar-proposal-financing.dto"
import useApplySolarCreditHuman from "~/api/hooks/useApplySolarCreditHuman"
import useFetchActiveLoanActivity from "~/api/hooks/useFetchActiveLoanActivity"
import useMarkApprovedSolarCreditHuman from "~/api/hooks/useMarkApprovedSolarCreditHuman"
import useReopenSolarCreditHuman from "~/api/hooks/useReopenSolarCreditHuman"
import useReplaceSolarCreditHuman from "~/api/hooks/useReplaceSolarCreditHuman"
import useSelectSolarLoanProduct from "~/api/hooks/useSelectSolarLoanProduct"
import { isSolarRefreshPricingRequiredError } from "~/api/solar-proposal-errors"

import { SolarRefreshPricingFix } from "./SolarRefreshPricingFix"
import type {
  CreditHumanStatusBundle,
  FinancingKindBundle,
} from "../ProposalSetup/financing/financing-kind-bundle"

export const SAVE_LOAN_PRODUCT_LABEL = `Save Loan Product`
export const LOAN_PRODUCT_SAVED_LABEL = `Loan Product saved. Refresh Pricing to calculate the payment.`
export const SAVE_LOAN_PRODUCT_ERROR_PROMPT = `Unable to save the Loan Product. Your selection is still here.`
export const SOLAR_PAYMENT_UNAVAILABLE_PROMPT = `Credit Human did not return a monthly payment. Refresh Pricing to try again.`

/**
 * Solar Proposal reads and commands for the shared Loan Product picker. Saving
 * records the selection only; Refresh Pricing writes the payment with the
 * price, so the picker never prices.
 */
export const solarLoanProductBundle: Pick<
  FinancingKindBundle<SolarProposalFinancingDto>,
  `loanProduct`
> = {
  loanProduct: {
    // Every product the seller may see, ineligible ones disabled with a reason.
    options: (financing) => financing.loanProducts,
    useSave: useSelectSolarLoanProduct,
    showOptionTerms: true,
    copy: {
      saveLabel: SAVE_LOAN_PRODUCT_LABEL,
      savingLabel: `Saving`,
      savedLabel: LOAN_PRODUCT_SAVED_LABEL,
      savedWithoutPaymentLabel: null,
      saveErrorPrompt: SAVE_LOAN_PRODUCT_ERROR_PROMPT,
      paymentUnavailablePrompt: SOLAR_PAYMENT_UNAVAILABLE_PROMPT,
      paymentRetryPrompt: null,
    },
  },
}

export { SOLAR_SUNGAGE_FINANCE_TYPE } from "./solar-finance-partner"

export const SOLAR_SUNGAGE_PAYMENT_UNAVAILABLE_PROMPT = `Sungage did not quote a monthly payment for this product.`

/**
 * The Loan Product picker for a Sungage plan: the same catalog picker over
 * the funding group's products, with Sungage's copy. Refresh Pricing asks
 * Sungage for the payment; saving never does.
 */
export const solarSungageLoanProductBundle: typeof solarLoanProductBundle = {
  loanProduct: {
    ...solarLoanProductBundle.loanProduct,
    copy: {
      ...solarLoanProductBundle.loanProduct.copy,
      paymentUnavailablePrompt: SOLAR_SUNGAGE_PAYMENT_UNAVAILABLE_PROMPT,
    },
  },
}

export const SOLAR_REFRESH_REQUIRED_PROMPT = `Refresh pricing first: the Quote no longer matches this proposal.`

/**
 * Solar Proposal Credit Human application commands for the shared status.
 * Apply and Replace never price; a stale Quote is refused with the inline
 * Refresh Pricing beside the reason.
 */
export const solarCreditHumanBundle: CreditHumanStatusBundle = {
  testIdPrefix: `solar`,
  creditHuman: {
    useApply: useApplySolarCreditHuman,
    useReopen: useReopenSolarCreditHuman,
    useReplace: useReplaceSolarCreditHuman,
    useMarkApproved: useMarkApprovedSolarCreditHuman,
    useActivity: useFetchActiveLoanActivity,
    isRefreshRequiredError: isSolarRefreshPricingRequiredError,
    RefreshPricing: SolarRefreshPricingFix,
    copy: { refreshRequired: SOLAR_REFRESH_REQUIRED_PROMPT },
  },
}
